/* eslint-disable jsx-a11y/alt-text */
/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2023-02-25 12:21:40
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import React, { Component } from 'react';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import * as sass from 'sass';
import { v4 } from 'uuid';
import { templateRender } from 'boost-template-engine';
import classNames from 'classnames';

import { DEVICE } from 'src/constants/theme-settings';
import { combineWidgets } from 'src/helpers/ssr-helper';
import { waitFor } from 'src/helpers/time-helper';
import { getShop } from 'src/helpers';
import { MENU_ID } from 'src/constants/theme-settings.jsx';
import Emitter, { EVENT_KEY } from 'src/libs/eventEmitter';
import {
  convertToDataThemeSetting,
  convertToShopifyThemeSetting
} from 'src/helpers/shopify-integration-settings-helper';
import { formatMoney } from 'src/helpers/money-format';
import noProductImage from 'src/assets/images/product/no-product-image.png';

type Props = {
  formValues: any,
  previewProductData: any,
  device: number
};

type ComposedProps = Props & WithI18nProps;

class AjaxCartPreview extends Component<ComposedProps> {
  constructor(props) {
    super(props);
    this.state = {
      previewHtml: '',
      compiling: false,
      prevCompiledCartStyle: _.get(props.formValues, 'setting.cart_setting.cartStyle')
    };
    this.widgetSettings = props.widgetSettings;
    this.initializedPreview = false;
    this.compiledSettingsCss = props.compiledSettingsCss;
  }
  componentDidMount() {
    this.buildDataPreviewProducts();
    this.handleRefreshPreview(this.props);
  }

  componentWillReceiveProps(nextProps) {
    const { formValues, device } = this.props;
    const { formValues: nextFormValues, device: nextDevice } = nextProps;
    const themeSettings = _.get(formValues, 'setting');
    const nextThemeSettings = _.get(nextFormValues, 'setting');

    if (
      !_.isEqual(themeSettings, nextThemeSettings) ||
      // Switch device between desktop + fullscreen and mobile need to refresh
      (device !== nextDevice &&
        (([DEVICE.DESKTOP, DEVICE.FULLSCREEN].includes(device) && nextDevice === DEVICE.MOBILE) ||
          (device === DEVICE.MOBILE && [DEVICE.DESKTOP, DEVICE.FULLSCREEN].includes(nextDevice))))
    ) {
      this.handleRefreshPreview(nextProps);
    }
  }

  getAllWidgets = props => {
    const { instantWidget } = props || this.props;
    const widgets = _.get(instantWidget, 'widgets', {});

    const allWidgets = Object.values(widgets).reduce((cur, next) => {
      return [...cur, ...next];
    }, []);
    return allWidgets;
  };

  buildDataPreviewProducts = () => {
    const { previewData, previewProductData, formValues } = this.props;
    const { shop = {} } = previewProductData;
    let total = 0;
    const convertedData = _.get(previewData, 'products', []).map(item => {
      const price = Number(item.variants[0]?.price) || item.price_min || item.price_max;
      total += price;
      return {
        id: item.id,
        handle: item.handle,
        image: _.isEmpty(_.get(item, 'images', {}))
          ? noProductImage
          : item.images[Object.keys(item.images)[0]],
        product_title: item.title,
        options_with_values: item.options_with_values.map(option => ({
          name: option.name,
          value: option.values[0]?.title
        })),
        quantity: 1,
        price: formatMoney(price, shop?.moneyFormat)
      };
    });

    this.previewProducts = {
      items: convertedData,
      item_count: convertedData.length,
      items_subtotal_price: formatMoney(total, shop?.moneyFormat),
      cartStyle: _.get(formValues, 'setting.cart_setting.cartStyle'),
      shape: _.get(formValues, 'setting.cart_setting.generalLayout.shape')
    };
  };

  handleRefreshPreview = props => {
    const { formValues: nextFormValues, scssContent: nextScssContent } = props;
    if (this.timeout) {
      clearTimeout(this.timeout);
    }
    this.onLoading();

    this.timeout = setTimeout(() => {
      const nextThemeSettings = _.get(nextFormValues, 'setting');

      const transformedData = convertToDataThemeSetting(nextThemeSettings);
      const shopMetaFieldSetting = convertToShopifyThemeSetting(
        transformedData,
        nextFormValues.pagination // Pass theme setting default value for case copying pagination button style infinite scroll from load more
      );
      console.log('shopMetaFieldSetting', shopMetaFieldSetting);

      this.widgetSettings = {
        ...this.widgetSettings,
        themeSettings: shopMetaFieldSetting
      };
      this.compileScss(this.widgetSettings, nextScssContent, props);
    }, 1000);
  };

  compileScss = async (widgetSettings, scssContent, props) => {
    if (!_.isEmpty(widgetSettings) && !_.isEmpty(scssContent)) {
      const sassMapString = `$settings: (${this.drill(widgetSettings)});\n`;
      const compiledScss = sassMapString + '\n' + scssContent;

      const { css } = sass.compileString(compiledScss);

      this.compiledSettingsCss = css;
      this.compileMainTemplate(props);
    }
  };

  drill = obj => {
    return Object.entries(obj).reduce((acc, [key, value]) => {
      const buildValue = value => {
        if (!value) return `'${key}': inherit,`;
        if (value && typeof value === 'object') return `'${key}': (${this.drill(value)}),`;
        return `'${key}': ${JSON.stringify(String(value))},`;
      };
      acc += buildValue(value);
      return acc;
    }, '');
  };

  getRequestParams = props => {
    const { selectedPage, device, formValues } = props || this.props;
    const limitPerPage = _.get(formValues, `setting.${MENU_ID.PRODUCT_LIST}.productsPerPage`, 16);
    const productsPerRowOnDesktop = _.get(
      formValues,
      `setting.${MENU_ID.PRODUCT_LIST}.productsPerRowOnDesktop`,
      3
    );
    const productsPerRowOnMobile = _.get(
      formValues,
      `setting.${MENU_ID.PRODUCT_LIST}.productsPerRowOnMobile`,
      2
    );

    const data = {
      t: Date.now(),
      sid: v4(),
      _: 'pf',
      shop: getShop(),
      page: 1,
      sort: 'Relevance',
      locale: 'en',
      event_type: 'init',
      build_filter_tree: 'true',
      handle: 'all',
      previewMode: true,
      pg: selectedPage?.value,
      limit: limitPerPage,
      viewAs: `grid--${
        device === DEVICE.DESKTOP ? productsPerRowOnDesktop : productsPerRowOnMobile
      }`
    };

    if (device === DEVICE.MOBILE) {
      data.device = 'mobile, tabletPortraitMax';
    }
    return data;
  };

  /**
   * compile & render preview template
   */
  compileMainTemplate = async props => {
    const { instantWidget, previewData, formValues } = props || this.props;

    const allWidgets = this.getAllWidgets();
    const mainWidgetId = instantWidget.widgetIds?.cart;
    const selectedWidget = instantWidget.widgets[mainWidgetId];

    if (
      !_.isEmpty(allWidgets) &&
      !_.isEmpty(selectedWidget) &&
      !_.isEmpty(this.widgetSettings) &&
      !_.isEmpty(this.compiledSettingsCss) &&
      !_.isEmpty(previewData)
    ) {
      const compiledTemplate = await combineWidgets(
        selectedWidget[0]?.widgetName, //'Add to cart',
        allWidgets,
        this.widgetSettings,
        this.compiledSettingsCss,
        instantWidget
      );
      const renderedTemplate = templateRender(compiledTemplate, {
        ...previewData,
        ...this.previewProducts,
        // items: _.get(previewData, 'products'),
        request: this.getRequestParams(props)
      });
      waitFor(100);
      this.setState({
        previewHtml: renderedTemplate,
        prevCompiledCartStyle: _.get(formValues, 'setting.cart_setting.cartStyle')
      });
    }
    this.offLoading();
  };

  onLoading = () => {
    if (!this.loading) {
      Emitter.emit(EVENT_KEY.LOADING_PREVIEW_THEME_SETTING);
      this.loading = true;
      this.setState({
        compiling: true
      });
    }
  };

  offLoading = () => {
    if (this.loading) {
      this.loading = false;
      Emitter.emit(EVENT_KEY.COMPLETE_PREVIEW_THEME_SETTING);
      this.setState({
        compiling: false
      });
    }
  };

  render() {
    const { device, formValues } = this.props;
    const { cart_setting = {} } = formValues?.setting || {};
    const cartStyle = cart_setting?.cartStyle;

    return (
      <div
        className={classNames('visual-editor-preview__overlay-cart', {
          mobile: device === DEVICE.MOBILE
        })}>
        <iframe
          id="preview-shopify-integration-setting-cart"
          className={classNames('preview-cart', {
            'preview-cart--mobile': device === DEVICE.MOBILE,
            'preview-cart--side': cartStyle === 'side',
            'preview-cart--fullscreen': device === DEVICE.FULLSCREEN
          })}
          title="Shopify integration setting preview"
          srcDoc={`
                <style>
                  .boost-sd__modal-backdrop {
                    background: transparent !important;
                  }
                  @media screen and (max-height: 800px) {
                    .boost-sd__cart:not(.boost-sd__cart--side) {
                      height: 520px !important;
                    }
                  }
                  @media screen and (max-width: 576px) {
                    .boost-sd__modal-container {
                      width: 375px !important;
                    }
                    .boost-sd__cart:not(.boost-sd__cart--side) {
                      height: 640px !important;
                    }
                  }
                  @media screen and (max-width: 576px) and (max-height: 800px) {
                    .boost-sd__cart:not(.boost-sd__cart--side) {
                      height: 520px !important;
                    }
                  }
                </style>
                <div class="boost-sd__preview-block">${this.state.previewHtml}</div>
              `}></iframe>
      </div>
    );
  }
}

export default withI18n()(AjaxCartPreview);
